/**
 * Push ingest session to the Chrome extension via externally_connectable.
 * Requires REACT_APP_EXTENSION_ID (unpacked extension id from chrome://extensions).
 */

export const INGEST_TOKEN_MESSAGE = 'STACKTRACK_INGEST_TOKEN';
export const CLEAR_INGEST_SESSION_MESSAGE = 'STACKTRACK_CLEAR_INGEST_SESSION';
export const GET_STATUS_MESSAGE = 'STACKTRACK_GET_STATUS';
export const SET_CONFIG_MESSAGE = 'STACKTRACK_SET_CONFIG';

const RESPONSE_TIMEOUT_MS = 4000;

/** @returns {typeof chrome | undefined} */
function getChromeRuntime() {
  return globalThis.chrome;
}

export function getExtensionId() {
  const id = String(process.env.REACT_APP_EXTENSION_ID || '').trim();
  return id || null;
}

/**
 * One round-trip to the extension. Never throws — an absent or unreachable
 * extension is an ordinary outcome, not an error.
 * @param {object} message
 * @returns {Promise<{ ok: boolean, reason?: string, response?: any }>}
 */
function sendToExtension(message) {
  const extensionId = getExtensionId();
  if (!extensionId) {
    return Promise.resolve({ ok: false, reason: 'extension_id_unset' });
  }
  const chromeApi = getChromeRuntime();
  if (!chromeApi?.runtime?.sendMessage) {
    return Promise.resolve({ ok: false, reason: 'chrome_runtime_unavailable' });
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve({ ok: false, reason: 'timeout' });
    }, RESPONSE_TIMEOUT_MS);

    try {
      chromeApi.runtime.sendMessage(extensionId, message, (response) => {
        clearTimeout(timeout);
        const err = chromeApi.runtime.lastError;
        if (err) {
          resolve({ ok: false, reason: err.message || 'sendMessage_failed' });
          return;
        }
        resolve(
          response?.ok
            ? { ok: true, response }
            : { ok: false, reason: response?.reason || 'rejected', response }
        );
      });
    } catch (err) {
      clearTimeout(timeout);
      resolve({ ok: false, reason: err?.message || 'sendMessage_threw' });
    }
  });
}

/**
 * @param {{ token: string, expiresAt?: string, deviceId: string, jti?: string }} session
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function sendIngestTokenToExtension(session) {
  const { ok, reason } = await sendToExtension({
    type: INGEST_TOKEN_MESSAGE,
    token: session.token,
    expiresAt: session.expiresAt,
    deviceId: session.deviceId,
    jti: session.jti,
  });
  return ok ? { ok: true } : { ok: false, reason };
}

/**
 * Ask the extension to clear its local ingest session (web Disconnect).
 */
export async function clearExtensionIngestSession() {
  const { ok, reason } = await sendToExtension({ type: CLEAR_INGEST_SESSION_MESSAGE });
  return ok ? { ok: true } : { ok: false, reason };
}

/**
 * Push the backend's effective feature configuration to the extension (AD-FP-1).
 *
 * Independent of token provisioning: this runs on every refresh cycle, including
 * when the extension's token is healthy and no session call is made.
 *
 * @param {{ version: number, issuedAt: string, features: Record<string, boolean> }} config
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function sendConfigToExtension(config) {
  const { ok, reason } = await sendToExtension({ type: SET_CONFIG_MESSAGE, config });
  return ok ? { ok: true } : { ok: false, reason };
}

/**
 * Ask the extension what session it already holds (AD-EA-3: the extension owns
 * the device id). A `connected` answer lets the caller skip provisioning
 * entirely, so a healthy extension costs zero API calls.
 *
 * @returns {Promise<{
 *   ok: boolean,
 *   reason?: string,
 *   connected: boolean,
 *   deviceId: string|null,
 *   jti: string|null,
 *   expiresAt: string|null,
 *   tokenVersion: number|null,
 * }>}
 */
export async function getExtensionStatus() {
  const { ok, reason, response } = await sendToExtension({ type: GET_STATUS_MESSAGE });
  if (!ok) {
    return {
      ok: false,
      reason,
      connected: false,
      deviceId: null,
      jti: null,
      expiresAt: null,
      tokenVersion: null,
    };
  }
  return {
    ok: true,
    connected: Boolean(response.connected),
    deviceId: response.deviceId || null,
    jti: response.jti || null,
    expiresAt: response.expiresAt || null,
    tokenVersion: response.tokenVersion != null ? Number(response.tokenVersion) : null,
  };
}
